import type { ChannelAvailabilityInfo, C2CHookResult, EmailHookResult } from '@vp/contact-channels'
import { AVAILABILITY_REASON, narrowReason, type NormalizedReason } from '@shared/channels'
import type { ContactChannel } from '../components/page'

const PREFIX = 'contact_us.page.steps.details.alerts'

export interface ChannelStatusResult {
  alertKey: string | null
  channelDisabled: boolean
}

const AVAILABLE: ChannelStatusResult = { alertKey: null, channelDisabled: false }

/** The alert copy keys a channel offers for each reason it can be unavailable for. */
interface AlertKeys {
  /** Absent for channels with no distinct after-hours state (email). */
  afterHours?: string
  emergency: string
  disabled: string
}

const CALLBACK_ALERTS: AlertKeys = {
  afterHours: `${PREFIX}.callback_after_hours`,
  emergency: `${PREFIX}.callback_emergency`,
  disabled: `${PREFIX}.callback_disabled`,
}

const EMAIL_ALERTS: AlertKeys = {
  emergency: `${PREFIX}.email_emergency`,
  disabled: `${PREFIX}.email_disabled`,
}

const alertFor = (reason: NormalizedReason | undefined, keys: AlertKeys): ChannelStatusResult => {
  if (reason === AVAILABILITY_REASON.OUT_OF_BUSINESS_HOURS && keys.afterHours) {
    return { alertKey: keys.afterHours, channelDisabled: true }
  }
  if (reason === AVAILABILITY_REASON.EMERGENCY) {
    return { alertKey: keys.emergency, channelDisabled: true }
  }
  return { alertKey: keys.disabled, channelDisabled: true }
}

/**
 * Resolve the alert copy key and disabled state for the page's channel step. Reason
 * comparison goes through the shared core's typed reason constants, so the misspelled
 * after-hours literal lives in exactly one place (`AVAILABILITY_REASON`) and an
 * out-of-contract reason normalizes to a defined unknown state rather than falling
 * through to "available".
 */
export const resolveChannelStatus = (
  channel: ContactChannel,
  callMe?: ChannelAvailabilityInfo<C2CHookResult | undefined>,
  email?: ChannelAvailabilityInfo<EmailHookResult | undefined>,
  isLoading?: boolean,
): ChannelStatusResult => {
  if (isLoading) {
    return { alertKey: null, channelDisabled: true }
  }

  const [availability, keys] = channel === 'callback' ? ([callMe, CALLBACK_ALERTS] as const) : ([email, EMAIL_ALERTS] as const)

  // An absent channel has not reported yet — treat it as available rather than alerting.
  if (!availability || availability.isAvailable) {
    return AVAILABLE
  }

  return alertFor(narrowReason(availability.reason), keys)
}

export const HELP_CENTER_FALLBACK_PATH = '/customer-care/help-center/'

export const isValidChannel = (channel?: ContactChannel): channel is ContactChannel => channel === 'callback' || channel === 'email'

/**
 * Resolve which channel step sequence the contact page should show, or `null` to redirect.
 * A ProAdvantage customer is always redirected (they reach chat/callback elsewhere);
 * otherwise a valid channel selector resolves its sequence and anything else redirects.
 */
export const resolveChannel = (channel: ContactChannel | undefined, isProAdvantageUser?: boolean): ContactChannel | null => {
  if (isProAdvantageUser || !isValidChannel(channel)) {
    return null
  }
  return channel
}
